import React from 'react';
import { AlertTriangle, Trash2, Send } from 'lucide-react';

/* ═══════════════════════════════════════════════════════
   Reusable Confirm Modal  (em-* design system)
   ═══════════════════════════════════════════════════════
   Props:
     open          – boolean, controls visibility
     onConfirm     – called when user clicks confirm
     onCancel      – called when user clicks cancel / backdrop
     title         – heading text  (default "Are you sure?")
     message       – body description  (string or JSX)
     confirmLabel  – confirm button text  (default "Confirm")
     cancelLabel   – cancel button text   (default "Cancel")
     variant       – 'danger' | 'warning' | 'primary'  (default 'danger')
     icon          – optional override icon component
     loading       – show spinner on confirm button
   ═══════════════════════════════════════════════════════ */

const VARIANT_CONFIG = {
  danger:  { btnClass: 'em-btn-danger',  iconBg: 'var(--em-danger-l,#fee2e2)',  iconColor: 'var(--em-danger,#ef4444)',  Icon: Trash2 },
  warning: { btnClass: 'em-btn-warning', iconBg: 'var(--em-warning-l,#fef3c7)', iconColor: 'var(--em-warning,#f59e0b)', Icon: AlertTriangle },
  primary: { btnClass: 'em-btn-primary', iconBg: 'var(--em-primary-l,#e0e7ff)', iconColor: 'var(--em-primary,#4f46e5)', Icon: Send },
};

const ConfirmModal = ({
  open = false,
  onConfirm,
  onCancel,
  title = 'Are you sure?',
  message = '',
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  icon: CustomIcon,
  loading = false,
  confirmDisabled = false,
}) => {
  if (!open) return null;

  const cfg = VARIANT_CONFIG[variant] || VARIANT_CONFIG.danger;
  const IconComp = CustomIcon || cfg.Icon;

  return (
    <div className="em-modal-overlay em-confirm-overlay" onClick={onCancel}>
      <div className="em-modal em-confirm-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <div className="em-modal-body em-confirm-body">
          <div className="em-confirm-icon" style={{ background: cfg.iconBg, color: cfg.iconColor }}>
            <IconComp size={24} />
          </div>
          <h3 className="em-confirm-title">{title}</h3>
          {message && <p className="em-confirm-msg">{message}</p>}
          {children}
        </div>
        <div className="em-modal-footer" style={{ justifyContent: 'center' }}>
          <button className="em-btn em-btn-secondary" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </button>
          <button className={`em-btn ${cfg.btnClass}`} onClick={onConfirm} disabled={loading || confirmDisabled}>
            {loading && (
              <span className="spin-icon" style={{ display: 'inline-flex', marginRight: 4 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
              </span>
            )}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
